import { useState, useEffect } from 'react';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import { ArrowLeft, CircleNotch, Lightning, Link } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import WindowNarrative from '../components/WindowNarrative';
import { NARRATIVES } from '../components/windowNarratives';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Window25MirrorComplement() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try { const res = await axios.get(`${API}/analysis/mirror-complement`); setData(res.data); }
      catch { toast.error('Failed to load'); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <CircleNotch size={40} className="animate-spin text-blue-600" />
    </div>
  );
  if (!data) return null;

  const conn = data.connections || {};
  const lastBalls = data.last_draw?.balls || [];

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" richColors />
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-[1600px] mx-auto px-6 py-3 flex items-center gap-4">
          <button onClick={() => navigate('/')} className="text-gray-500 hover:text-gray-900" data-testid="back-btn">
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-gray-900">Number Connections — {data.current_year}</h1>
            <p className="text-xs text-gray-500">Numbers that co-appear in same or consecutive draws in {data.current_year}. Auto-updates with each draw.</p>
          </div>
          <div className="text-right text-[10px] text-gray-400">
            <p>Last: #{data.last_draw?.draw} ({data.last_draw?.date})</p>
            <p>{data.cy_draws} draws in {data.current_year}</p>
          </div>
        </div>
      </header>
      <main className="max-w-[1600px] mx-auto px-6 py-6 space-y-6">

        <WindowNarrative windowId="W25" {...NARRATIVES.W25} className="mb-5" />

        {/* Active Signal from Last Draw */}
        {conn.active_signals && Object.keys(conn.active_signals).length > 0 && (
          <div className="bg-gradient-to-r from-amber-600 to-orange-600 rounded-lg p-5 text-white" data-testid="active-signal">
            <div className="flex items-center gap-2 mb-3">
              <Lightning size={24} weight="fill" />
              <h2 className="font-bold text-xl">Active Signal — Next Draw Candidates</h2>
            </div>
            <p className="text-xs text-white/70 mb-4">Last draw balls [{lastBalls.join(', ')}] — based on {data.current_year} connections, these partners are most likely to appear next:</p>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {lastBalls.map(num => {
                const partners = conn.active_signals?.[num] || [];
                if (!partners.length) return (
                  <div key={num} className="bg-white/10 rounded-lg p-3">
                    <div className="text-center">
                      <span className="inline-flex w-10 h-10 rounded-full bg-white/20 items-center justify-center font-mono font-bold text-xl">{num}</span>
                      <p className="text-[10px] text-white/50 mt-1">No strong connections</p>
                    </div>
                  </div>
                );
                return (
                  <div key={num} className="bg-white/10 rounded-lg p-3">
                    <div className="text-center mb-2">
                      <span className="inline-flex w-10 h-10 rounded-full bg-white/30 items-center justify-center font-mono font-bold text-xl">{num}</span>
                    </div>
                    <div className="flex gap-1 flex-wrap justify-center">
                      {partners.slice(0, 5).map(p => (
                        <span key={p.number} className="inline-flex items-center gap-0.5 bg-white/20 rounded-full px-2 py-0.5 text-xs font-bold">
                          {p.number} <span className="text-white/60 text-[9px]">({p.count}x)</span>
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Top Connected Pairs */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="top-pairs">
          <div className="p-4 border-b border-gray-200">
            <h2 className="font-bold text-gray-900">Top Connected Pairs — {data.current_year}</h2>
            <p className="text-[10px] text-gray-400">Pairs appearing 3+ times together (same draw + consecutive draws) in {data.current_year}</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-4 py-2 text-left font-bold text-gray-500">Pair</th>
                  <th className="px-4 py-2 text-center font-bold text-gray-500">Combined</th>
                  <th className="px-4 py-2 text-center font-bold text-gray-500">Same Draw</th>
                  <th className="px-4 py-2 text-center font-bold text-amber-600">Next Draw</th>
                  <th className="px-4 py-2 text-center font-bold text-gray-500">A apps</th>
                  <th className="px-4 py-2 text-center font-bold text-gray-500">B apps</th>
                  <th className="px-4 py-2 text-left font-bold text-gray-500">Strength</th>
                </tr>
              </thead>
              <tbody>
                {(conn.top_pairs || []).map((p, idx) => {
                  const maxPossible = Math.min(p.a_apps, p.b_apps);
                  const strength = maxPossible > 0 ? Math.round(p.combined / maxPossible * 100) : 0;
                  const isActive = lastBalls.includes(p.a) || lastBalls.includes(p.b);
                  return (
                    <tr key={idx} className={`border-b border-gray-100 hover:bg-gray-50 ${isActive ? 'bg-amber-50' : ''}`}>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-1">
                          <span className={`inline-flex w-8 h-8 rounded-full items-center justify-center font-mono font-bold text-xs ${isActive && lastBalls.includes(p.a) ? 'bg-amber-500 text-white' : 'bg-[#002FA7] text-white'}`}>{p.a}</span>
                          <Link size={14} className="text-gray-400" />
                          <span className={`inline-flex w-8 h-8 rounded-full items-center justify-center font-mono font-bold text-xs ${isActive && lastBalls.includes(p.b) ? 'bg-amber-500 text-white' : 'bg-[#002FA7] text-white'}`}>{p.b}</span>
                          {isActive && <span className="ml-2 text-[9px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">ACTIVE</span>}
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-center font-mono font-bold text-lg text-gray-900">{p.combined}</td>
                      <td className="px-4 py-2.5 text-center font-mono text-gray-600">{p.same}</td>
                      <td className="px-4 py-2.5 text-center font-mono font-bold text-amber-700">{p.next}</td>
                      <td className="px-4 py-2.5 text-center font-mono text-gray-500">{p.a_apps}</td>
                      <td className="px-4 py-2.5 text-center font-mono text-gray-500">{p.b_apps}</td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          <div className="w-20 bg-gray-100 rounded-full h-3">
                            <div className={`h-3 rounded-full ${strength >= 80 ? 'bg-emerald-500' : strength >= 50 ? 'bg-amber-400' : 'bg-gray-300'}`} style={{ width: `${Math.min(strength, 100)}%` }} />
                          </div>
                          <span className="text-[10px] font-mono font-bold text-gray-600">{strength}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Hub Numbers */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="hub-numbers">
          <div className="p-4 border-b border-gray-200">
            <h2 className="font-bold text-gray-900">Hub Numbers — Most Connected in {data.current_year}</h2>
            <p className="text-[10px] text-gray-400">Numbers with the most partners (3+ co-occurrences). Higher connections = more central to {data.current_year} draw patterns.</p>
          </div>
          <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            {(conn.hubs || []).slice(0, 12).map((hub, idx) => {
              const isInLast = lastBalls.includes(hub.number);
              return (
                <div key={hub.number} className={`rounded-lg border p-3 ${isInLast ? 'border-amber-300 bg-amber-50' : idx < 3 ? 'border-indigo-200 bg-indigo-50' : 'border-gray-200 bg-gray-50'}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <span className={`inline-flex w-10 h-10 rounded-full items-center justify-center font-mono font-bold text-lg ${isInLast ? 'bg-amber-500 text-white' : idx < 3 ? 'bg-indigo-600 text-white' : 'bg-gray-300 text-gray-700'}`}>{hub.number}</span>
                    <div>
                      <p className="text-xs font-bold text-gray-800">{hub.appearances}x appeared</p>
                      <p className="text-[10px] text-gray-500">{hub.connections} connections</p>
                    </div>
                    {isInLast && <span className="ml-auto text-[9px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">IN LAST DRAW</span>}
                  </div>
                  <div className="flex gap-1 flex-wrap">
                    {hub.partners.slice(0, 8).map(p => (
                      <span key={p.number} className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded ${lastBalls.includes(p.number) ? 'bg-amber-200 text-amber-800' : 'bg-white text-gray-600 border border-gray-200'}`}>
                        {p.number}({p.count}x)
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
}
